/**
 * Iter 47 · Central de Notificações — Página completa
 * Lista tudo com filtros + "marcar todas" + envio manual (Diretoria).
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Bell, Check, CheckCheck, Trash2, ExternalLink, Send, Megaphone } from "lucide-react";

const TIPO_ICON = {
  meta_alcancada: "🎉",
  lead_pendente: "🎯",
  lead_direcionado: "🎯",
  captacao_auto: "📥",
  captacao_resumo: "📊",
  manual: "📣",
};

const PRIO_COR = {
  urgente: "bg-red-100 text-red-800 border-red-300",
  importante: "bg-amber-100 text-amber-800 border-amber-300",
  normal: "bg-slate-100 text-slate-700 border-slate-200",
};

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "agora";
  if (s < 3600) return `${Math.floor(s / 60)} min atrás`;
  if (s < 86400) return `${Math.floor(s / 3600)} h atrás`;
  return `${Math.floor(s / 86400)} d atrás`;
}

export default function Notificacoes() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("todas"); // todas | nao_lidas
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({ titulo: "", corpo: "", escopo: "meu_time", prioridade: "normal" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const q = filtro === "nao_lidas" ? "?nao_lidas=true&limit=200" : "?limit=200";
    api.get(`/notificacoes${q}`)
      .then((r) => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filtro]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onNew = () => load();
    window.addEventListener("ws:notificacao_nova", onNew);
    return () => window.removeEventListener("ws:notificacao_nova", onNew);
  }, [load]);

  const naoLidas = useMemo(() => items.filter((i) => !i.lida).length, [items]);

  const marcarLida = async (nid) => {
    try {
      await api.post(`/notificacoes/${nid}/lida`);
      setItems((prev) => prev.map((n) => (n.id === nid ? { ...n, lida: true } : n)));
    } catch { toast.error("Erro"); }
  };

  const marcarTodas = async () => {
    try {
      await api.post("/notificacoes/marcar-todas-lidas");
      setItems((prev) => prev.map((n) => ({ ...n, lida: true })));
      toast.success("Todas marcadas como lidas");
    } catch { toast.error("Erro"); }
  };

  const excluir = async (nid) => {
    try {
      await api.delete(`/notificacoes/${nid}`);
      setItems((prev) => prev.filter((n) => n.id !== nid));
    } catch { toast.error("Erro"); }
  };

  const enviarManual = async () => {
    if (!manual.titulo.trim() || !manual.corpo.trim()) {
      toast.error("Preencha título e corpo"); return;
    }
    setBusy(true);
    try {
      const r = await api.post("/notificacoes/manual", manual);
      toast.success(`✓ Enviada para ${r.data.enviadas} pessoa${r.data.enviadas > 1 ? "s" : ""}`);
      setManualOpen(false);
      setManual({ titulo: "", corpo: "", escopo: "meu_time", prioridade: "normal" });
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => d.msg).join(" · ") : (detail || "Erro"));
    } finally { setBusy(false); }
  };

  const podeMandarManual = user?.role === "diretoria" || user?.role === "supervisao";
  const isMaster = user?.role === "diretoria" && !user?.nucleo_id;

  return (
    <div className="space-y-6" data-testid="notificacoes-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-2">
            <Bell className="w-8 h-8 text-blue-600" />
            Notificações
            {naoLidas > 0 && (
              <span className="ml-1 inline-flex items-center bg-red-500 text-white rounded-full px-2.5 py-0.5 text-sm font-bold animate-pulse">
                {naoLidas}
              </span>
            )}
          </h1>
          <p className="text-slate-500 text-sm">
            Metas batidas, leads MKT, captações e avisos da diretoria.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setFiltro("todas")}
            className={`text-sm font-bold px-3 py-1.5 rounded-lg transition ${filtro === "todas" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
            data-testid="btn-filtro-todas"
          >Todas</button>
          <button
            onClick={() => setFiltro("nao_lidas")}
            className={`text-sm font-bold px-3 py-1.5 rounded-lg transition ${filtro === "nao_lidas" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
            data-testid="btn-filtro-nao-lidas"
          >Só não lidas</button>
          {naoLidas > 0 && (
            <Button variant="outline" onClick={marcarTodas} data-testid="btn-marcar-todas">
              <CheckCheck className="w-4 h-4 mr-1" /> Marcar todas
            </Button>
          )}
          {podeMandarManual && (
            <Button onClick={() => setManualOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-enviar-manual">
              <Megaphone className="w-4 h-4 mr-1" /> Enviar aviso
            </Button>
          )}
        </div>
      </div>

      <Card className="p-0 rounded-2xl overflow-hidden">
        {loading && items.length === 0 && (
          <div className="p-6 space-y-4" data-testid="notif-carregando">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex gap-3 animate-pulse">
                <div className="w-8 h-8 rounded-full bg-slate-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-2/5 bg-slate-200 rounded" />
                  <div className="h-4 w-4/5 bg-slate-200 rounded" />
                  <div className="h-3 w-full bg-slate-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-10 text-center text-slate-400 text-sm" data-testid="notif-lista-vazia">
            {filtro === "nao_lidas" ? "Nenhuma notificação não lida." : "Sem notificações."}
          </div>
        )}
        {items.map((n) => (
          <div
            key={n.id}
            className={`px-4 py-3 border-b border-slate-100 flex gap-3 items-start ${n.lida ? "" : "bg-blue-50/40"}`}
            data-testid={`notif-row-${n.id}`}
          >
            <div className="text-2xl leading-none pt-0.5">{TIPO_ICON[n.tipo] || "🔔"}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] uppercase tracking-widest font-bold rounded px-1.5 py-0.5 border ${PRIO_COR[n.prioridade] || PRIO_COR.normal}`}>
                  {n.prioridade}
                </span>
                <div className={`${n.lida ? "text-slate-700" : "font-bold text-slate-900"}`}>{n.titulo}</div>
              </div>
              <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{n.corpo}</div>
              <div className="text-[11px] text-slate-400 mt-1">
                {timeAgo(n.criado_em)}
                {n.criado_por_nome ? ` · por ${n.criado_por_nome}` : ""}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {n.link_url && (
                <button
                  onClick={() => { if (!n.lida) marcarLida(n.id); nav(n.link_url); }}
                  className="p-1.5 rounded-lg hover:bg-blue-100 text-blue-700"
                  title="Abrir"
                  data-testid={`notif-abrir-${n.id}`}
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
              )}
              {!n.lida && (
                <button
                  onClick={() => marcarLida(n.id)}
                  className="p-1.5 rounded-lg hover:bg-emerald-100 text-emerald-700"
                  title="Marcar como lida"
                  data-testid={`notif-check-${n.id}`}
                >
                  <Check className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => excluir(n.id)}
                className="p-1.5 rounded-lg hover:bg-red-100 text-red-700"
                title="Excluir"
                data-testid={`notif-excluir-${n.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </Card>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-lg" data-testid="modal-manual">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-emerald-600" /> Enviar aviso ao time
            </DialogTitle>
            <DialogDescription>
              Diretores enviam para o próprio núcleo; Master (Diretoria sem núcleo) pode alcançar toda a empresa.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs font-bold uppercase text-slate-600">Título *</label>
              <Input
                value={manual.titulo}
                onChange={(e) => setManual({ ...manual, titulo: e.target.value })}
                maxLength={120}
                placeholder="Ex.: Sprint sexta 15h — todos!"
                data-testid="in-titulo"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-slate-600">Corpo *</label>
              <Textarea
                rows={5}
                value={manual.corpo}
                onChange={(e) => setManual({ ...manual, corpo: e.target.value })}
                maxLength={2000}
                placeholder="Escreva sua mensagem para o time..."
                data-testid="in-corpo"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase text-slate-600">Enviar para</label>
                <Select value={manual.escopo} onValueChange={(v) => setManual({ ...manual, escopo: v })}>
                  <SelectTrigger data-testid="sel-escopo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="meu_time">Meu núcleo</SelectItem>
                    {isMaster && <SelectItem value="todos">🌎 Todos (Master)</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-slate-600">Prioridade</label>
                <Select value={manual.prioridade} onValueChange={(v) => setManual({ ...manual, prioridade: v })}>
                  <SelectTrigger data-testid="sel-prioridade"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="importante">Importante</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)} disabled={busy}>Cancelar</Button>
            <Button onClick={enviarManual} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-enviar-aviso">
              <Send className="w-4 h-4 mr-1" /> {busy ? "Enviando..." : "Enviar aviso"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
